/**
 * Shared CSV export utilities for BKG module.
 * RFC 4180 compliant: comma-delimited, double-quote escaped.
 */

export const convertToCSV = (rows: Array<Record<string, string>>): string => {
  if (!rows.length) return "";

  const headers = Object.keys(rows[0]);
  const headerRow = headers.join(",");
  const dataRows = rows
    .map((row) =>
      headers
        .map((key) => {
          const val = row[key] ?? "";
          const str = typeof val === "string" ? val : String(val);
          return `"${str.replace(/"/g, '""')}"`;
        })
        .join(","),
    )
    .join("\n");

  return `${headerRow}\n${dataRows}`;
};

export const downloadCSV = (csv: string, filename: string): void => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

type ContactSource<TItem, TContact> = {
  label?: string;
  getContacts: (item: TItem) => TContact[] | TContact | unknown;
  getPerson?: (contact: TContact) => any;
};

type ExtractContactsConfig<TItem, TContact> = {
  entityNameColumn: string;
  contactNameColumn: string;
  contactTitleColumn: string;
  contactEmailColumn: string;
  sourceColumn?: string;
  dedupeByEmail?: boolean;
  requireEmail?: boolean;
  getEntityName: (item: TItem) => string | undefined;
  getEntityId?: (item: TItem) => string | undefined;
  getContactName: (person: any) => string | undefined;
  getContactTitle: (person: any) => string | undefined;
  getContactEmail: (person: any) => string | undefined;
  sources: Array<ContactSource<TItem, TContact>>;
};

export const extractContacts = <TItem, TContact>(
  results: TItem[],
  config: ExtractContactsConfig<TItem, TContact>,
): Array<Record<string, string>> => {
  const normalizeToArray = (value: unknown): TContact[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value as TContact[];

    if (typeof value === "object" && value !== null && "value" in value) {
      const nested = (value as { value?: unknown }).value;
      return Array.isArray(nested)
        ? (nested as TContact[])
        : nested
          ? ([nested] as TContact[])
          : [];
    }

    return [value as TContact];
  };

  const rows: Array<Record<string, string>> = [];
  const seen = new Set<string>();

  for (const item of results || []) {
    const entityName = config.getEntityName(item) || "";
    const entityId = config.getEntityId?.(item) || entityName || "unknown";

    for (const source of config.sources) {
      const contacts = normalizeToArray(source.getContacts(item));

      for (const contact of contacts) {
        const person = source.getPerson ? source.getPerson(contact) : contact;
        if (!person) continue;

        const email = (config.getContactEmail(person) || "").trim();
        if (config.requireEmail !== false && !email) {
          continue;
        }

        const dedupeKey =
          config.dedupeByEmail !== false && email
            ? `${entityId}::${email.toLowerCase()}`
            : "";

        if (dedupeKey && seen.has(dedupeKey)) {
          continue;
        }

        const row: Record<string, string> = {
          [config.entityNameColumn]: entityName,
          [config.contactNameColumn]: config.getContactName(person) || "",
          [config.contactTitleColumn]: config.getContactTitle(person) || "",
          [config.contactEmailColumn]: email,
        };

        if (config.sourceColumn) {
          row[config.sourceColumn] = source.label || "";
        }

        rows.push(row);

        if (dedupeKey) {
          seen.add(dedupeKey);
        }
      }
    }
  }

  return rows;
};
